// A league's rules, from the screens: the rules in force, every version, a
// change saved and its preview — for a practice league and a real one, one
// shape either way (PLAN-2026-09-26-every-screen-and-crayon P1–P2).
//
// A real league reads and writes `/api/leagues/:id/settings` and its two
// neighbors; the practice league is answered by the simulator, built into the
// same `LeagueRulesView` the server serves, so the Rules and Settings screens
// never ask which world they are in. The one difference a screen can see is
// honest rather than hidden: `fixed` lists what a real league cannot change
// once it exists, and for the practice league, whose simulator rebuilds itself
// around any change, only its divisions, which it plays without.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  LeagueRulesView as LeagueRulesViewSchema,
  RulesPreviewView as RulesPreviewViewSchema,
  RulesVersionsView as RulesVersionsViewSchema,
  describeChanges,
  describeSettings,
  scoringTable,
  startingPositions,
  type Issue,
  type LeagueRulesView,
  type LeagueSettings,
  type RulesPreviewView,
  type RulesVersionsView,
  type Snapshot,
} from '@fsp/domain';
import { leagueRef } from '@fsp/sim';
import { getSport } from '../sports/registry';
import { messageOf, send } from './api';
import { useLeagues } from './LeaguesProvider';
import { useLeagueSource, useLeagueView } from './leagueSource';
import { thenRefresh } from './moves';
import { leagueIdFromPath } from './route';
import { useSession } from './session';
import { useSim } from './SimProvider';

function useLeagueIdInRoute(): string | null {
  const { pathname } = useLocation();
  return useMemo(() => leagueIdFromPath(pathname), [pathname]);
}

const PRACTICE_DIVISIONS_FIXED = [
  'schedule.divisions',
  'schedule.divisionGamesFirst',
  'playoffs.seeding',
].map((path) => ({ path, why: 'The practice league plays without divisions.' }));

/** The sim keeps an unlock in the reason ("[unlocked scoring] …"); the wire keeps it apart. */
function splitReason(raw: string): { reason: string; unlocked: string[] } {
  const m = /^\[unlocked ([^\]]*)\]\s*(.*)$/s.exec(raw);
  if (m === null) return { reason: raw, unlocked: [] };
  return {
    reason: m[2] ?? '',
    unlocked: (m[1] ?? '')
      .split(',')
      .map((x) => x.trim())
      .filter(Boolean),
  };
}

/** The rules in force: `GET /leagues/:id/settings`, or the practice league's. */
export function useLeagueRules(): Snapshot<LeagueRulesView> {
  const { state, version, now } = useSim();
  const leagueId = useLeagueIdInRoute();
  const source = useLeagueSource();
  const simulated = useMemo((): LeagueRulesView | null => {
    if (source !== 'sim') return null;
    const sport = getSport(state.sport as never);
    const drafted = state.status === 'in-season';
    const last = state.settingsVersions[state.settingsVersions.length - 1];
    return {
      league: leagueRef(state),
      version: last?.version ?? 1,
      settings: state.settings,
      document: describeSettings(state.settings, sport),
      scoringTable: scoringTable(state.settings.scoring, sport, {
        positions: startingPositions(state.settings.roster, sport),
      }),
      canEdit: state.isCommissioner,
      // The practice league fixes nothing, at any stage.
      stage: drafted ? 'in-season' : 'before-draft',
      drafted,
      locked: drafted ? state.settings.governance.lockedAfterDraft : [],
      // THE PRACTICE LEAGUE PLAYS WITHOUT DIVISIONS: its simulator places no
      // team in one, so the editor says so rather than offering a change that
      // would do nothing (PLAN-2026-10-29-divisions §5).
      fixed: PRACTICE_DIVISIONS_FIXED,
      appliesFrom: drafted
        ? { period: state.currentPeriod + 1, label: `Week ${state.currentPeriod + 1}` }
        : { period: 1, label: 'the start' },
      // The practice league's commissioner saves; it has no league to vote.
      changesGoToVote: false,
      proposal: null,
      teams: [],
    };
    // `version` is the simulator's change counter: the state object is the
    // same one after a save, so the counter is what moves this.
  }, [source, state, version]);
  return useLeagueView(
    leagueId === null ? null : `/leagues/${leagueId}/settings`,
    LeagueRulesViewSchema,
    simulated,
    new Date(now).toISOString(),
  );
}

/** Every version newest first, each change said as the rules say it. */
export function useRulesVersions(): Snapshot<RulesVersionsView> {
  const { state, version, now } = useSim();
  const leagueId = useLeagueIdInRoute();
  const source = useLeagueSource();
  const simulated = useMemo((): RulesVersionsView | null => {
    if (source !== 'sim') return null;
    const sport = getSport(state.sport as never);
    const all = state.settingsVersions;
    return {
      versions: all
        .map((v, i) => {
          const { reason, unlocked } = splitReason(v.reason);
          const before = all[i - 1];
          const changes =
            before === undefined ? [] : describeChanges(before.settings, v.settings, sport);
          return {
            version: v.version,
            createdAt: v.createdAt,
            author: v.createdBy === 'system' ? 'The league' : v.createdBy,
            reason,
            effectiveFromPeriod: v.effectiveFromPeriod,
            scoringFrom:
              v.effectiveFromPeriod > 1 && changes.some((c) => c.section === 'scoring')
                ? `Week ${v.effectiveFromPeriod}`
                : null,
            unlocked,
            changes,
          };
        })
        .reverse(),
    };
  }, [source, state, version]);
  return useLeagueView(
    leagueId === null ? null : `/leagues/${leagueId}/settings/versions`,
    RulesVersionsViewSchema,
    simulated,
    new Date(now).toISOString(),
  );
}

export type SaveOutcome =
  | { ok: true; version: number; appliesFrom: string | null }
  | { ok: false; message: string; issues: Issue[] };

export type PreviewOutcome =
  { ok: true; preview: RulesPreviewView } | { ok: false; message: string };

export type VoteOutcome = { ok: true } | { ok: false; message: string };

export interface RulesActions {
  save(input: {
    settings: LeagueSettings;
    reason: string;
    unlock: boolean;
    basedOn: number;
  }): Promise<SaveOutcome>;
  /** Put the change to the league's vote rather than saving it (B6). A real league only. */
  propose(input: {
    settings: LeagueSettings;
    reason: string;
    unlock: boolean;
    basedOn: number;
  }): Promise<SaveOutcome>;
  /** The commissioner withdraws the change up for a vote. */
  withdraw(): Promise<VoteOutcome>;
  /** A manager's yes or no on it. */
  vote(choice: 'yes' | 'no'): Promise<VoteOutcome>;
  preview(settings: LeagueSettings): Promise<PreviewOutcome>;
  /**
   * Put a team in one of the saved divisions, at once (PLAN-2026-10-29-divisions
   * §3). A real league only; the practice league plays without divisions.
   */
  placeTeam(teamId: string, divisionId: string | null): Promise<VoteOutcome>;
  /**
   * Name the team another plays in a rivalry week, or none, at once
   * (PLAN-2026-10-29-the-schedule-before-the-season §2). A real league only.
   */
  placeRival(teamId: string, rivalTeamId: string | null): Promise<VoteOutcome>;
}

const UNREACHABLE = 'Could not reach the site. Nothing was saved.';

/** Saving and previewing, for whichever world the league in the route is. */
export function useRulesActions(): RulesActions {
  const sim = useSim();
  const { session } = useSession();
  const leagueId = useLeagueIdInRoute();
  const source = useLeagueSource();
  const hub = useLeagues();

  const save = useCallback<RulesActions['save']>(
    async (input) => {
      if (source === 'sim') {
        const result = sim.saveSettings(input.settings, {
          reason: input.reason,
          unlock: input.unlock,
          by: session?.displayName ?? 'Commissioner',
        });
        if (!result.ok) {
          return {
            ok: false,
            message: result.issues[0]?.message ?? 'Could not save.',
            issues: result.issues,
          };
        }
        return {
          ok: true,
          version: result.version.version,
          appliesFrom:
            sim.state.status === 'in-season' ? `Week ${result.version.effectiveFromPeriod}` : null,
        };
      }
      if (leagueId === null) return { ok: false, message: 'No league here.', issues: [] };
      const reply = await thenRefresh(
        leagueId,
        send('POST', `/leagues/${leagueId}/settings`, input, LeagueRulesViewSchema),
      );
      if (reply.status === 'ok') {
        // The league's name lives on its hub card too, and the shell titles
        // the league from it: a rename is read back there as well.
        hub.reload();
        return {
          ok: true,
          version: reply.data.version,
          appliesFrom: reply.data.drafted ? (reply.data.appliesFrom?.label ?? null) : null,
        };
      }
      const issues: Issue[] =
        reply.status === 'refused'
          ? (reply.details?.issues ?? []).map((i) => ({
              path: i.path,
              message: i.message,
              severity: 'error' as const,
            }))
          : [];
      return { ok: false, message: messageOf(reply, 'Could not save.', UNREACHABLE), issues };
    },
    [source, sim, session, leagueId, hub],
  );

  const propose = useCallback<RulesActions['propose']>(
    async (input) => {
      // The practice league has no league to vote; its commissioner saves.
      if (source === 'sim' || leagueId === null)
        return { ok: false, message: 'Only a real league votes on its rules.', issues: [] };
      const reply = await thenRefresh(
        leagueId,
        send('POST', `/leagues/${leagueId}/settings/proposal`, input, LeagueRulesViewSchema),
      );
      if (reply.status === 'ok')
        return { ok: true, version: reply.data.version, appliesFrom: null };
      const issues: Issue[] =
        reply.status === 'refused'
          ? (reply.details?.issues ?? []).map((i) => ({
              path: i.path,
              message: i.message,
              severity: 'error' as const,
            }))
          : [];
      return {
        ok: false,
        message: messageOf(reply, 'Could not put that to a vote.', UNREACHABLE),
        issues,
      };
    },
    [source, leagueId],
  );

  /** A vote's own acts: read back everywhere, the rules page first. */
  const voteAct = useCallback(
    async (method: 'POST' | 'DELETE', path: string, body: unknown): Promise<VoteOutcome> => {
      if (source === 'sim' || leagueId === null)
        return { ok: false, message: 'Only a real league votes on its rules.' };
      const reply = await thenRefresh(
        leagueId,
        send(method, `/leagues/${leagueId}/settings/${path}`, body, LeagueRulesViewSchema),
      );
      if (reply.status === 'ok') {
        // A vote that passed renames nothing today, but a change of the
        // league's name is a rule like any other, and the hub shows the name.
        hub.reload();
        return { ok: true };
      }
      return {
        ok: false,
        message: messageOf(
          reply,
          'That did not work.',
          'Could not reach the site, so this may not have happened. Look again.',
        ),
      };
    },
    [source, leagueId, hub],
  );
  const withdraw = useCallback<RulesActions['withdraw']>(
    () => voteAct('DELETE', 'proposal', undefined),
    [voteAct],
  );
  const vote = useCallback<RulesActions['vote']>(
    (choice) => voteAct('POST', 'proposal/vote', { vote: choice }),
    [voteAct],
  );

  const preview = useCallback<RulesActions['preview']>(
    async (settings) => {
      if (source === 'sim') {
        const p = sim.previewSettings(settings);
        return {
          ok: true,
          preview: {
            period: p.status === 'pre' ? null : p.period,
            label: p.status === 'pre' ? null : `Week ${p.period}`,
            status: p.status === 'pre' ? 'none' : p.status,
            rows: p.rows,
          },
        };
      }
      if (leagueId === null) return { ok: false, message: 'No league here.' };
      const reply = await send(
        'POST',
        `/leagues/${leagueId}/settings/preview`,
        { settings },
        RulesPreviewViewSchema,
      );
      if (reply.status === 'ok') return { ok: true, preview: reply.data };
      return { ok: false, message: messageOf(reply, 'Could not preview that.') };
    },
    [source, sim, leagueId],
  );

  const placeTeam = useCallback<RulesActions['placeTeam']>(
    async (teamId, divisionId) => {
      if (source === 'sim' || leagueId === null)
        return { ok: false, message: 'The practice league plays without divisions.' };
      const reply = await thenRefresh(
        leagueId,
        send(
          'PUT',
          `/leagues/${leagueId}/teams/${teamId}/division`,
          { divisionId },
          LeagueRulesViewSchema,
        ),
      );
      if (reply.status === 'ok') return { ok: true };
      return {
        ok: false,
        message: messageOf(
          reply,
          'That team was not moved.',
          'Could not reach the site. Nothing was moved.',
        ),
      };
    },
    [source, leagueId],
  );

  const placeRival = useCallback<RulesActions['placeRival']>(
    async (teamId, rivalTeamId) => {
      if (source === 'sim' || leagueId === null)
        return { ok: false, message: 'The practice league has no rivals.' };
      const reply = await thenRefresh(
        leagueId,
        send(
          'PUT',
          `/leagues/${leagueId}/teams/${teamId}/rival`,
          { rivalTeamId },
          LeagueRulesViewSchema,
        ),
      );
      if (reply.status === 'ok') return { ok: true };
      return {
        ok: false,
        message: messageOf(
          reply,
          'That rival was not named.',
          'Could not reach the site. Nothing was changed.',
        ),
      };
    },
    [source, leagueId],
  );

  return useMemo(
    () => ({ save, propose, withdraw, vote, preview, placeTeam, placeRival }),
    [save, propose, withdraw, vote, preview, placeTeam, placeRival],
  );
}

/**
 * A preview that follows the draft: asked again a moment after the candidate's
 * scoring or roster stops changing, the last answer kept on screen while the
 * next is in flight, and an answer that arrives after a newer question is
 * dropped rather than shown over it.
 */
export function useRulesPreview(
  actions: RulesActions,
  candidate: LeagueSettings,
  enabled: boolean,
): { preview: RulesPreviewView | null; failure: string | null; pending: boolean } {
  const key = enabled ? JSON.stringify([candidate.scoring, candidate.roster]) : '';
  const [answer, setAnswer] = useState<{
    key: string;
    preview: RulesPreviewView | null;
    failure: string | null;
  }>({ key: '', preview: null, failure: null });
  // BY REF, NOT BY DEPENDENCY: the actions are rebuilt whenever the
  // simulator's clock ticks (every second on the practice league), and as a
  // dependency that re-asked the server every second (P2's review).
  const latest = useRef({ actions, candidate });
  latest.current = { actions, candidate };
  useEffect(() => {
    if (!enabled) return;
    let live = true;
    const timer = setTimeout(() => {
      const { actions, candidate } = latest.current;
      void actions.preview(candidate).then((out) => {
        if (!live) return;
        setAnswer(
          out.ok
            ? { key, preview: out.preview, failure: null }
            : { key, preview: null, failure: out.message },
        );
      });
    }, 300);
    return () => {
      live = false;
      clearTimeout(timer);
    };
    // The key is the candidate's scoring and roster; nothing else moves a preview.
  }, [key, enabled]);
  return {
    preview: enabled ? answer.preview : null,
    failure: enabled ? answer.failure : null,
    pending: enabled && answer.key !== key,
  };
}
